import type { ItemData, ItemAttribute } from '@thatopen/fragments'
import type { PropertyGroup } from '@/viewer/types'
import { prettyIfcClass } from './names'

function isAttribute(v: ItemAttribute | ItemData[] | undefined): v is ItemAttribute {
  return !!v && !Array.isArray(v) && typeof v === 'object' && 'value' in v
}

function attrValue(item: ItemData, key: string): unknown {
  const v = item[key]
  return isAttribute(v) ? v.value : undefined
}

export function formatValue(val: unknown): string {
  if (val === null || val === undefined || val === '') return '—'
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') {
    if (Number.isInteger(val)) return val.toString()
    return Number(val.toFixed(4)).toString()
  }
  if (typeof val === 'object') return JSON.stringify(val)
  return String(val)
}

/** Attribute keys that are internal to fragments and not useful to show. */
function isInternalKey(key: string): boolean {
  return key === 'type' || key.startsWith('_')
}

/** Item name ('Name' attribute) or ''. */
export function itemName(item: ItemData): string {
  const name = attrValue(item, 'Name')
  return typeof name === 'string' ? name : name != null ? String(name) : ''
}

/** IFC class from the item data ('IfcBeam'), or '' when unknown. */
export function itemClass(item: ItemData): string {
  const cat = attrValue(item, '_category') ?? attrValue(item, 'type')
  return typeof cat === 'string' && cat ? prettyIfcClass(cat) : ''
}

/**
 * Converts getItemsData() output (attributes + relations) into display groups:
 * first 'Attributes', then one group per related entity (property sets,
 * quantity sets, type objects, materials...).
 */
export function itemToGroups(item: ItemData): PropertyGroup[] {
  const groups: PropertyGroup[] = []
  const base: [string, string][] = []
  const guid = attrValue(item, '_guid')

  for (const [key, val] of Object.entries(item)) {
    if (Array.isArray(val)) {
      for (const related of val) {
        const relName = attrValue(related, 'Name')
        const relCat = attrValue(related, '_category')
        const groupName =
          (typeof relName === 'string' && relName) ||
          (typeof relCat === 'string' && prettyIfcClass(relCat)) ||
          key
        const props: [string, string][] = []
        for (const [k, v] of Object.entries(related)) {
          if (Array.isArray(v)) {
            // Nested relations, e.g. HasProperties / Quantities of a property set
            for (const nested of v) {
              const propName = attrValue(nested, 'Name')
              const propVal =
                attrValue(nested, 'NominalValue') ??
                attrValue(nested, 'Value') ??
                attrValue(nested, 'LengthValue') ??
                attrValue(nested, 'AreaValue') ??
                attrValue(nested, 'VolumeValue') ??
                attrValue(nested, 'CountValue') ??
                attrValue(nested, 'WeightValue')
              if (propVal !== undefined) {
                props.push([typeof propName === 'string' && propName ? propName : k, formatValue(propVal)])
              }
            }
          } else if (isAttribute(v) && !isInternalKey(k) && k !== 'Name') {
            props.push([k, formatValue(v.value)])
          }
        }
        if (props.length > 0) groups.push({ name: groupName, props })
      }
    } else if (isAttribute(val) && !isInternalKey(key)) {
      base.push([key, formatValue(val.value)])
    }
  }

  if (typeof guid === 'string' && !base.some(([k]) => k === 'GlobalId')) {
    base.unshift(['GlobalId', guid])
  }
  if (base.length > 0) groups.unshift({ name: 'Attributes', props: base })
  return groups
}
